/**
 * Language as a property of the URL.
 *
 * The marketing site serves English at the root and Greek under `/el`. This
 * module owns that one fact and nothing else: it is pure string work, with no
 * DOM, no router, no i18next and no knowledge of which pages actually exist in
 * which language. That last question belongs to `src/content/pages/registry.js`,
 * which is the single source of truth for availability and imports these
 * helpers rather than restating them.
 *
 * The separation matters because the two questions have different consumers.
 * `useAnalyticsContext` and `internalNavigation` need to know what language a
 * path is in; neither should have to pull the whole page registry (and, through
 * it, all fourteen guide content modules) into its module graph to find out.
 *
 * ── Why `en` has no prefix ────────────────────────────────────────────────
 * English URLs are live and indexed. `/wedding-planning` stays exactly where it
 * is; Greek is added alongside at `/el/wedding-planning`. There is deliberately
 * no `/en/` tree — introducing one would mean 301-ing every indexed URL on the
 * site to buy nothing but symmetry, which `localizedPath` provides for free.
 */

/** The default language, and the one served without a prefix. */
export const DEFAULT_LANGUAGE = "en";

/** Every language the site can serve, in the order alternates are emitted. */
export const LANGUAGES = ["en", "el"];

/**
 * The path segment each language lives under. English is the empty string —
 * that is the whole of the Option A URL strategy, stated once.
 */
export const LANGUAGE_PREFIX = {
  en: "",
  el: "/el",
};

export const isSupportedLanguage = (language) => LANGUAGES.includes(language);

/** Every language that actually occupies a path segment. */
const PREFIXED_LANGUAGES = LANGUAGES.filter((lng) => LANGUAGE_PREFIX[lng]);

/**
 * The language a pathname *explicitly declares*, or `null` when it declares
 * none. `/el/wedding-planning` returns `"el"`; `/wedding-planning` returns
 * `null` rather than `"en"`.
 *
 * The distinction is the reason this function exists separately from
 * `getLanguageFromPathname`, and it is load-bearing in two places:
 *
 *   - **i18next detection.** An explicit `/el` prefix must beat `?lang=` and
 *     localStorage. An unprefixed path must NOT, or the `?lang=el` links that
 *     `app/` and `invite/` already send here would stop working — see the
 *     detector in `src/locales/i18n.js`.
 *   - **Analytics.** A `/el` path reports `el` on the strength of the URL
 *     alone; an unprefixed path defers to the language actually rendered.
 *
 * Matching is exact on the segment: `/elephant` declares nothing.
 */
export const getExplicitLanguageFromPathname = (pathname) => {
  if (typeof pathname !== "string") return null;
  for (const language of PREFIXED_LANGUAGES) {
    const prefix = LANGUAGE_PREFIX[language];
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return language;
    }
  }
  return null;
};

/**
 * The language a pathname is served in.
 *
 *   /                      → "en"
 *   /wedding-planning      → "en"
 *   /el                    → "el"
 *   /el/                   → "el"
 *   /el/wedding-planning   → "el"
 *   /el/guides/foo         → "el"
 *   /elephant              → "en"
 */
export const getLanguageFromPathname = (pathname) =>
  getExplicitLanguageFromPathname(pathname) ?? DEFAULT_LANGUAGE;

/**
 * A pathname with its language prefix removed — the language-neutral path that
 * identifies the page itself, and the key everything else looks pages up by.
 *
 *   /el/wedding-planning   → "/wedding-planning"
 *   /wedding-planning      → "/wedding-planning"
 *   /el                    → "/"
 *   /el/                   → "/"
 *
 * Exactly one prefix is removed, so `/el/el/x` normalises to `/el/x` — which no
 * registry entry and no route matches. A doubled prefix stays broken rather
 * than being silently repaired into a working page at a URL that should not
 * exist.
 */
export const stripLanguagePrefix = (pathname) => {
  const language = getExplicitLanguageFromPathname(pathname);
  if (!language) return pathname;
  const rest = pathname.slice(LANGUAGE_PREFIX[language].length);
  return rest === "" ? "/" : rest;
};

/**
 * A language-neutral path, prefixed for a language. The inverse of
 * `stripLanguagePrefix`, and idempotent — it normalises its input first, so
 * feeding it an already-prefixed path cannot produce `/el/el/…`.
 *
 *   ("/wedding-planning", "el")  → "/el/wedding-planning"
 *   ("/", "el")                  → "/el/"
 *   ("/wedding-planning", "en")  → "/wedding-planning"
 *
 * The Greek root keeps its trailing slash, mirroring the English `/` it is the
 * counterpart of. Every other path has none, matching the site's existing
 * no-trailing-slash convention (which `vercel.json` already enforces with a
 * redirect per page).
 *
 * This says nothing about whether the localized page EXISTS — it is pure string
 * work. Use `localizedPath` from the page registry when availability matters,
 * which is almost always.
 */
export const withLanguagePrefix = (pathname, language) => {
  const base = stripLanguagePrefix(pathname);
  const prefix = LANGUAGE_PREFIX[language] ?? "";
  if (!prefix) return base;
  return base === "/" ? `${prefix}/` : `${prefix}${base}`;
};
